import * as React from 'react';

import {Button} from 'shells/button/button';
import {ErrorMessage} from 'shells/form/error-message';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {Body} from 'shells/typography/body';

import type {
    GetSuggestedReplies_suggestedReplies as SuggestedReplies,
    GetSuggestedReplies_suggestedReplies_replies as SuggestedReply,
} from 'nutshell-graphql-types';

import {analyticsClient} from '../../../../analytics-client';

import {SuggestedReplyWrapper} from '../../ai-replies';

import {useAcceptEmailReplySuggestion} from '../graphql/hooks/use-accept-email-reply-suggestion';

import {getInitialSelectedSuggestedReply, getShouldShowSection} from './helpers';

import styles from './suggested-replies-section.module.css';

type Props = {
    suggestedReplies: SuggestedReplies | null | undefined;
    selectedSuggestedReplyId: string | null | undefined;
    isLoading?: boolean;
    onSuggestedReplyInserted: (reply: SuggestedReply) => void;
};

export function SuggestedRepliesSection(props: Props) {
    const [selectedSuggestedReply, setSelectedSuggestedReply] = React.useState<
        SuggestedReply | null | undefined
    >(getInitialSelectedSuggestedReply(props.suggestedReplies, props.selectedSuggestedReplyId));
    const [shouldShowSection, setShouldShowSection] = React.useState<boolean>(false);
    const [acceptErrorMessage, setAcceptErrorMessage] = React.useState<string | null | undefined>(
        null
    );

    const {acceptEmailReplySuggestion} = useAcceptEmailReplySuggestion();

    React.useEffect(() => {
        setShouldShowSection(getShouldShowSection(props.suggestedReplies, props.isLoading));
    }, [props.suggestedReplies, props.isLoading]);

    React.useEffect(() => {
        setSelectedSuggestedReply(
            getInitialSelectedSuggestedReply(props.suggestedReplies, props.selectedSuggestedReplyId)
        );
    }, [props.suggestedReplies, props.selectedSuggestedReplyId]);

    const handleSuggestedReplyInserted = () => {
        if (selectedSuggestedReply) {
            acceptEmailReplySuggestion({
                suggestedReplyId: selectedSuggestedReply.id,
            })
                .then(() => {
                    props.onSuggestedReplyInserted(selectedSuggestedReply);
                    setSelectedSuggestedReply(null);
                    setShouldShowSection(false);
                    Timeline.refreshTimeline();
                })
                .catch(() => {
                    setAcceptErrorMessage('Error inserting suggested reply');
                });
        }
    };

    const handleSuggestedReplyCleared = () => {
        setSelectedSuggestedReply(null);
        setAcceptErrorMessage(null);
        analyticsClient.track(analyticsClient.EVENTS.TRACK_AI_EMAIL_REPLY_REMOVED);
    };

    const handleSelectedSuggestedReplyChanged = (suggestedReply: SuggestedReply) => {
        setSelectedSuggestedReply(suggestedReply);
        setAcceptErrorMessage(null);
        analyticsClient.track(analyticsClient.EVENTS.TRACK_AI_EMAIL_REPLY_STAGED);
    };

    return (
        <>
            {shouldShowSection && (
                <div className='px-16 py-8 border-bottom border-box'>
                    <StackLegacy spacing={8}>
                        <SuggestedReplyWrapper
                            suggestedReplies={props.suggestedReplies}
                            onCardClicked={handleSelectedSuggestedReplyChanged}
                            variant='full'
                            isLoading={props.isLoading}
                        />
                        {selectedSuggestedReply && (
                            <>
                                <div className={styles['pending-suggested-reply']}>
                                    <Body size='small' maintainLineBreaks={true}>
                                        {selectedSuggestedReply.body}
                                    </Body>
                                </div>
                                <div className='flex gap-8 pt-8'>
                                    <Button
                                        variant='secondary'
                                        onClick={handleSuggestedReplyCleared}
                                    >
                                        Clear
                                    </Button>
                                    <Button
                                        variant='ai-blue'
                                        onClick={handleSuggestedReplyInserted}
                                    >
                                        Insert
                                    </Button>
                                    {acceptErrorMessage && (
                                        <div className='flex align-center'>
                                            <ErrorMessage>{acceptErrorMessage}</ErrorMessage>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </StackLegacy>
                </div>
            )}
        </>
    );
}
